import React, { useEffect, useState } from "react";
import { getPropertyBidders, Bidder } from "../api";

// BiddersPanel — "Who's bidding here": the upset bidders / purchasers captured
// for a property from public court record (GET /properties/:id/bidders).
//
// Design notes (mirrors RiskFlagList):
//   - Public data, NOT Pro-gated — works for logged-out users, same as the
//     property detail itself.
//   - Most properties have no bidders on file. Render NOTHING on empty so the
//     detail page doesn't grow an empty box. Loading / error are silent too —
//     never crash or spew an error on the detail page; degrade to "no panel".
//   - Markup matches the existing detail panels (section-label + detail-group),
//     so styling stays token-only and consistent with the redesign.

interface BiddersPanelProps {
  propertyId: string;
}

const BiddersPanel: React.FC<BiddersPanelProps> = ({ propertyId }) => {
  const [bidders, setBidders] = useState<Bidder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPropertyBidders(propertyId)
      .then((list) => {
        if (!cancelled) setBidders(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // Never crash the detail page — degrade to "no bidders shown".
        if (!cancelled) setBidders([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // Loading / empty / error: render nothing. Most properties have no bidders;
  // an empty box would be noise.
  if (loading || bidders.length === 0) return null;

  return (
    <>
      <div className="section-label">Who's bidding here</div>
      <ol className="bidder-list">
        {bidders.map((b, i) => (
          <li key={`${b.name}-${i}`} className="bidder-row">
            <span className="bidder-name">{b.name}</span>
            {b.role && <span className="bidder-role">{b.role}</span>}
            {b.address && <span className="bidder-address">{b.address}</span>}
          </li>
        ))}
      </ol>
      <p className="secondary bidder-note">
        Upset bidders and purchasers on file from public court record.
      </p>
    </>
  );
};

export default BiddersPanel;
